import React, { useState, useEffect } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import MultipleChoiceQuiz from '@/components/games/MultipleChoiceQuiz';
import FillInTheBlank from '@/components/games/FillInTheBlank';
import MatchingGame from '@/components/games/MatchingGame';
import DrawingCanvas from '@/components/games/DrawingCanvas';
import SortingGame from '@/components/games/SortingGame';
import PuzzleGame from '@/components/games/PuzzleGame';
import DragDropGame from '@/components/DragDropGame';
import {
  ArrowLeft, Gamepad2, HelpCircle, Pencil, Puzzle, Brain, Loader2, Star, Layers, Grid3x3
} from 'lucide-react';

const InteractiveGames = () => {
  const { childId } = useParams();
  const [searchParams] = useSearchParams();
  const gameType = searchParams.get('type') || 'quiz';
  const subject = searchParams.get('subject') || 'alphabets';
  
  const { api } = useAuth();
  const [child, setChild] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeGame, setActiveGame] = useState(gameType);
  const [gameCompleted, setGameCompleted] = useState(false);
  const [finalScore, setFinalScore] = useState(0);

  useEffect(() => {
    fetchChild();
  }, [childId]);

  const fetchChild = async () => {
    try {
      const res = await api.get(`/children/${childId}`);
      setChild(res.data);
    } catch (error) {
      console.error('Failed to fetch child:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleGameComplete = async (score, attempts) => {
    setFinalScore(score);
    setGameCompleted(true);
    
    try {
      await api.post(`/activities/game-complete?child_id=${childId}&game_type=${activeGame}&subject=${subject}&score=${score}`);
      toast.success(`Great job! You earned ${Math.round(score / 10)} coins!`);
    } catch (error) {
      console.error('Failed to save progress:', error);
    }
  };

  const resetGame = () => {
    setGameCompleted(false);
    setFinalScore(0);
  };

  // Sample game data based on subject
  const getGameData = () => {
    const data = {
      alphabets: {
        quiz: [
          { question: 'What letter is this? A', options: ['A', 'B', 'C', 'D'], answer: 'A', hint: 'It looks like a triangle with a line!' },
          { question: 'Which letter comes after B?', options: ['A', 'C', 'D', 'E'], answer: 'C', hint: 'A, B, _...' },
          { question: 'What letter does "Cat" start with?', options: ['A', 'B', 'C', 'K'], answer: 'C', hint: 'Meow!' },
          { question: 'Which letter looks like a snake?', options: ['S', 'O', 'L', 'I'], answer: 'S', hint: 'Sssss...' },
          { question: 'What is the first letter of the alphabet?', options: ['Z', 'A', 'B', 'C'], answer: 'A', hint: 'It starts the ABC song!' },
        ],
        fillBlank: [
          { sentence: 'The letter ___ comes after A.', answer: 'B', hint: 'A, _, C', wordBank: ['A', 'B', 'C', 'D'] },
          { sentence: 'Cat starts with the letter ___.', answer: 'C', hint: 'Meow!', wordBank: ['C', 'K', 'A', 'T'] },
          { sentence: 'The last letter of the alphabet is ___.', answer: 'Z', hint: 'Zzzzz', wordBank: ['X', 'Y', 'Z', 'A'] },
        ],
        matching: [
          { item1: 'A', item2: 'Apple' },
          { item1: 'B', item2: 'Ball' },
          { item1: 'C', item2: 'Cat' },
          { item1: 'D', item2: 'Dog' },
        ],
        drawing: [
          { character: 'A', type: 'letter', name: 'Letter A', example: 'Apple' },
          { character: 'B', type: 'letter', name: 'Letter B', example: 'Ball' },
          { character: 'C', type: 'letter', name: 'Letter C', example: 'Cat' },
          { character: 'D', type: 'letter', name: 'Letter D', example: 'Dog' },
          { character: 'E', type: 'letter', name: 'Letter E', example: 'Elephant' },
        ],
      },
      counting: {
        quiz: [
          { question: 'How many stars? ⭐⭐⭐', options: ['2', '3', '4', '5'], answer: '3', hint: 'Count each star!' },
          { question: 'What comes after 5?', options: ['4', '6', '7', '8'], answer: '6', hint: '1, 2, 3, 4, 5, _...' },
          { question: 'How many fingers on one hand?', options: ['4', '5', '6', '10'], answer: '5', hint: 'Look at your hand!' },
          { question: 'What is 2 + 2?', options: ['3', '4', '5', '6'], answer: '4', hint: 'Count on your fingers!' },
          { question: 'Count the apples: 🍎🍎🍎🍎', options: ['3', '4', '5', '6'], answer: '4', hint: 'One by one!' },
        ],
        fillBlank: [
          { sentence: '2 + 1 = ___', answer: '3', hint: 'Count on your fingers', wordBank: ['2', '3', '4', '5'] },
          { sentence: '___ comes before 5.', answer: '4', hint: '1, 2, 3, _, 5', wordBank: ['3', '4', '5', '6'] },
          { sentence: 'There are ___ days in a week.', answer: '7', hint: 'Monday through Sunday', wordBank: ['5', '6', '7', '8'] },
        ],
        matching: [
          { item1: '1', item2: 'One ●' },
          { item1: '2', item2: 'Two ●●' },
          { item1: '3', item2: 'Three ●●●' },
          { item1: '4', item2: 'Four ●●●●' },
        ],
        drawing: [
          { character: '1', type: 'number', name: 'Number 1', example: 'One apple' },
          { character: '2', type: 'number', name: 'Number 2', example: 'Two birds' },
          { character: '3', type: 'number', name: 'Number 3', example: 'Three cats' },
          { character: '4', type: 'number', name: 'Number 4', example: 'Four dogs' },
          { character: '5', type: 'number', name: 'Number 5', example: 'Five fingers' },
        ],
      },
      reading: {
        quiz: [
          { question: 'Which word rhymes with "cat"?', options: ['dog', 'hat', 'bird', 'fish'], answer: 'hat', hint: 'It ends with "-at"' },
          { question: 'What word is "d-o-g"?', options: ['cat', 'dog', 'pig', 'cow'], answer: 'dog', hint: 'Woof woof!' },
          { question: 'Which word starts with "B"?', options: ['cat', 'apple', 'ball', 'dog'], answer: 'ball', hint: 'You can bounce it!' },
        ],
        fillBlank: [
          { sentence: 'The ___ says "meow".', answer: 'cat', hint: 'A furry pet', wordBank: ['dog', 'cat', 'bird', 'fish'] },
          { sentence: 'I like to read a ___.', answer: 'book', hint: 'It has pages', wordBank: ['book', 'ball', 'toy', 'car'] },
        ],
        matching: [
          { item1: 'cat', item2: '🐱' },
          { item1: 'dog', item2: '🐕' },
          { item1: 'fish', item2: '🐟' },
          { item1: 'bird', item2: '🐦' },
        ],
        drawing: [
          { character: 'cat', type: 'word', name: 'Word: cat', example: '🐱' },
          { character: 'dog', type: 'word', name: 'Word: dog', example: '🐕' },
          { character: 'sun', type: 'word', name: 'Word: sun', example: '☀️' },
        ],
      },
      math: {
        quiz: [
          { question: '5 + 3 = ?', options: ['6', '7', '8', '9'], answer: '8', hint: 'Use your fingers!' },
          { question: '10 - 4 = ?', options: ['4', '5', '6', '7'], answer: '6', hint: 'Start at 10, count back 4' },
          { question: '2 × 3 = ?', options: ['5', '6', '7', '8'], answer: '6', hint: '3 + 3 = ?' },
          { question: 'What shape has 4 equal sides?', options: ['circle', 'triangle', 'square', 'rectangle'], answer: 'square', hint: 'Like a box!' },
        ],
        fillBlank: [
          { sentence: '7 + 2 = ___', answer: '9', hint: 'Almost 10!', wordBank: ['8', '9', '10', '11'] },
          { sentence: '15 - 5 = ___', answer: '10', hint: 'Count back 5', wordBank: ['8', '9', '10', '11'] },
          { sentence: '4 × 2 = ___', answer: '8', hint: '4 + 4', wordBank: ['6', '7', '8', '9'] },
        ],
        matching: [
          { item1: '2+2', item2: '4' },
          { item1: '3+3', item2: '6' },
          { item1: '5+5', item2: '10' },
          { item1: '4+4', item2: '8' },
        ],
        drawing: [
          { character: '+', type: 'symbol', name: 'Plus Sign', example: 'For addition' },
          { character: '-', type: 'symbol', name: 'Minus Sign', example: 'For subtraction' },
          { character: '=', type: 'symbol', name: 'Equals Sign', example: 'Shows the answer' },
        ],
        sorting: {
          items: [
            { id: 'even-2', label: '2', categoryId: 'even' },
            { id: 'odd-3', label: '3', categoryId: 'odd' },
            { id: 'even-4', label: '4', categoryId: 'even' },
            { id: 'odd-5', label: '5', categoryId: 'odd' },
            { id: 'even-6', label: '6', categoryId: 'even' },
            { id: 'odd-7', label: '7', categoryId: 'odd' },
            { id: 'even-8', label: '8', categoryId: 'even' },
            { id: 'odd-9', label: '9', categoryId: 'odd' },
          ],
          categories: [
            { id: 'even', label: 'Even Numbers', icon: '➗' },
            { id: 'odd', label: 'Odd Numbers', icon: '🔢' },
          ],
          title: 'Sort Even and Odd Numbers!'
        },
        puzzle: {
          pieces: [
            { id: 'p1', label: '1+1=2' },
            { id: 'p2', label: '2+2=4' },
            { id: 'p3', label: '3+3=6' },
            { id: 'p4', label: '4+4=8' },
            { id: 'p5', label: '5+5=10' },
          ],
          targetOrder: ['p1', 'p2', 'p3', 'p4', 'p5'],
          title: 'Order the Equations!',
          instructions: 'Put the math facts in order from smallest to largest'
        }
      },
    };
    
    // Add default sorting and puzzle to all subjects if not defined
    Object.keys(data).forEach(subj => {
      if (!data[subj].sorting) {
        data[subj].sorting = {
          items: [
            { id: 'upper-A', label: 'A', categoryId: 'upper' },
            { id: 'lower-a', label: 'a', categoryId: 'lower' },
            { id: 'upper-B', label: 'B', categoryId: 'upper' },
            { id: 'lower-b', label: 'b', categoryId: 'lower' },
            { id: 'upper-C', label: 'C', categoryId: 'upper' },
            { id: 'lower-c', label: 'c', categoryId: 'lower' },
          ],
          categories: [
            { id: 'upper', label: 'Uppercase', icon: '⬆️' },
            { id: 'lower', label: 'Lowercase', icon: '⬇️' },
          ],
          title: 'Sort Upper and Lowercase!'
        };
      }
      if (!data[subj].puzzle) {
        data[subj].puzzle = {
          pieces: [
            { id: 'a', label: 'A' },
            { id: 'b', label: 'B' },
            { id: 'c', label: 'C' },
            { id: 'd', label: 'D' },
            { id: 'e', label: 'E' },
          ],
          targetOrder: ['a', 'b', 'c', 'd', 'e'],
          title: 'Put the Alphabet in Order!',
          instructions: 'Arrange the letters A, B, C, D, E'
        };
      }
    });
    
    return data[subject] || data.alphabets;
  };

  const gameData = getGameData();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
      </div>
    );
  }

  // Game completed screen
  if (gameCompleted) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-yellow-50 to-white flex items-center justify-center p-4">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center"
        >
          <div className="w-32 h-32 mx-auto mb-6 rounded-full bg-gradient-to-r from-yellow-400 to-orange-500 flex items-center justify-center">
            <Star className="w-16 h-16 text-white" />
          </div>
          <h1 className="font-fredoka text-4xl font-bold text-yellow-800 mb-2">Amazing!</h1>
          <p className="text-2xl text-yellow-600 mb-6">You scored {finalScore}%</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button onClick={resetGame} className="bg-yellow-500 hover:bg-yellow-600">
              Play Again
            </Button>
            <Link to={`/learn/${childId}`}>
              <Button variant="outline">Back to Learning Hub</Button>
            </Link>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-emerald-50 to-white">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-emerald-200 sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to={`/learn/${childId}`}>
                <Button variant="ghost" size="icon">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
              </Link>
              <div className="flex items-center gap-2">
                <Gamepad2 className="w-6 h-6 text-emerald-500" />
                <h1 className="font-fredoka text-xl font-bold text-emerald-800 capitalize">
                  {subject} Games
                </h1>
              </div>
            </div>
            {child && (
              <div className="flex items-center gap-2 bg-emerald-100 rounded-full px-3 py-1">
                <Star className="w-4 h-4 text-emerald-500" />
                <span className="text-sm font-bold text-emerald-700">{child.total_xp || 0} XP</span>
              </div>
            )}
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 py-8">
        <Tabs value={activeGame} onValueChange={(v) => { setActiveGame(v); resetGame(); }}>
          <TabsList className="grid w-full grid-cols-6 mb-6">
            <TabsTrigger value="quiz" className="gap-1 text-xs" data-testid="tab-quiz">
              <HelpCircle className="w-4 h-4" /> Quiz
            </TabsTrigger>
            <TabsTrigger value="fillBlank" className="gap-1 text-xs" data-testid="tab-fill">
              <Pencil className="w-4 h-4" /> Fill
            </TabsTrigger>
            <TabsTrigger value="matching" className="gap-1 text-xs" data-testid="tab-match">
              <Grid3x3 className="w-4 h-4" /> Match
            </TabsTrigger>
            <TabsTrigger value="sorting" className="gap-1 text-xs" data-testid="tab-sort">
              <Layers className="w-4 h-4" /> Sort
            </TabsTrigger>
            <TabsTrigger value="puzzle" className="gap-1 text-xs" data-testid="tab-puzzle">
              <Puzzle className="w-4 h-4" /> Puzzle
            </TabsTrigger>
            <TabsTrigger value="drawing" className="gap-1 text-xs" data-testid="tab-draw">
              <Brain className="w-4 h-4" /> Draw
            </TabsTrigger>
          </TabsList>

          <TabsContent value="quiz">
            <MultipleChoiceQuiz
              questions={gameData.quiz}
              onComplete={handleGameComplete}
              childId={childId}
            />
          </TabsContent>

          <TabsContent value="fillBlank">
            <FillInTheBlank
              questions={gameData.fillBlank}
              onComplete={handleGameComplete}
              childId={childId}
            />
          </TabsContent>

          <TabsContent value="matching">
            <MatchingGame
              pairs={gameData.matching}
              onComplete={handleGameComplete}
              childId={childId}
              title={`Match the ${subject}!`}
            />
          </TabsContent>

          <TabsContent value="sorting">
            <SortingGame
              items={gameData.sorting?.items || []}
              categories={gameData.sorting?.categories || []}
              onComplete={handleGameComplete}
              title={gameData.sorting?.title || 'Sort the items!'}
            />
          </TabsContent>

          <TabsContent value="puzzle">
            <PuzzleGame
              pieces={gameData.puzzle?.pieces || []}
              targetOrder={gameData.puzzle?.targetOrder || []}
              onComplete={handleGameComplete}
              title={gameData.puzzle?.title || 'Complete the puzzle!'}
              instructions={gameData.puzzle?.instructions}
            />
          </TabsContent>

          <TabsContent value="drawing">
            <DrawingCanvas
              items={gameData.drawing}
              onComplete={handleGameComplete}
              childId={childId}
              title={`Trace the ${subject}!`}
            />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default InteractiveGames;
